import React, { useEffect, useState } from 'react';
import { Alert, Card, Col, Row, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaBook, FaDatabase, FaFileAlt, FaLayerGroup } from 'react-icons/fa';
import { fetchAiChatbotIndexingOptions } from '../../../utils/aiChatbotIndexingMode';

const AiChatbotSetupHub = () => {
  const navigate = useNavigate();
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const opts = await fetchAiChatbotIndexingOptions();
        if (active) setOptions(opts);
      } catch {
        if (active) {
          setOptions({
            canAccessSetup: false,
            canIndexStandard: false,
            canIndexCustom: false,
          });
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading…
      </div>
    );
  }

  const schemaCards = [];
  if (options?.canIndexStandard) {
    schemaCards.push({
      key: 'standard',
      path: '/setup/aichatbot/schema/standard',
      title: 'Index Standard Schema',
      icon: FaLayerGroup,
      text: 'Product builder: choose standard CRM objects, save the catalog, and index for AI search.',
    });
    schemaCards.push({
      key: 'prompts',
      path: '/setup/aichatbot/prompts',
      title: 'Manage Platform Prompts',
      icon: FaFileAlt,
      text: 'Edit shared system prompts for SQL, documents, email, and WhatsApp generation.',
    });
  }
  if (options?.canIndexCustom) {
    schemaCards.push({
      key: 'custom',
      path: '/setup/aichatbot/schema/custom',
      title: 'Index Custom Schema',
      icon: FaDatabase,
      text: 'Choose custom objects and custom fields for this tenant, save the catalog, and index for AI search.',
    });
  }

  return (
    <div>
      <p className="text-muted mb-4">
        Configure how the AI Chatbot understands your data. Dictionary terms and schema prompts
        apply immediately to new chat queries. Schema indexing builds vector search from your AI
        schema catalog. Product builders can also edit platform-wide system prompts.
      </p>

      {!options?.canAccessSetup && (
        <Alert variant="info" className="mb-4">
          AI Chatbot setup is not enabled for this tenant.
        </Alert>
      )}

      <Row className="g-4">
        {schemaCards.map((card) => (
          <Col md={6} key={card.key}>
            <Card
              className="ai-chatbot-hub-card shadow-sm h-100"
              onClick={() => navigate(card.path)}
            >
              <Card.Body>
                <Card.Title className="h5 d-flex align-items-center">
                  <card.icon className="me-2 text-primary" />
                  {card.title}
                </Card.Title>
                <Card.Text className="text-muted small mb-0">{card.text}</Card.Text>
              </Card.Body>
            </Card>
          </Col>
        ))}
        <Col md={6}>
          <Card
            className="ai-chatbot-hub-card shadow-sm h-100"
            onClick={() => navigate('/setup/aichatbot/schema-prompts')}
          >
            <Card.Body>
              <Card.Title className="h5 d-flex align-items-center">
                <FaFileAlt className="me-2 text-primary" />
                Manage Schema Prompts
              </Card.Title>
              <Card.Text className="text-muted small mb-0">
                Add tenant-specific AI instructions — for example, how closed-won stages are
                labeled or rules for custom objects. Stored in your tenant schema.
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card
            className="ai-chatbot-hub-card shadow-sm h-100"
            onClick={() => navigate('/setup/aichatbot/dictionary')}
          >
            <Card.Body>
              <Card.Title className="h5 d-flex align-items-center">
                <FaBook className="me-2 text-primary" />
                Manage AI Dictionary
              </Card.Title>
              <Card.Text className="text-muted small mb-0">
                Map words your users say to CRM concepts — for example, &quot;company&quot; means
                the account table. Changes apply without re-indexing.
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AiChatbotSetupHub;
